import React, { useEffect, useRef, useState } from 'react';
import {
    Dialog, DialogContent, DialogActions,
    Button, TextField, Box, Typography, ButtonBase, IconButton,
} from '@mui/material';
import CircularProgress from '@material-ui/core/CircularProgress';
import PeopleAltIcon from '@material-ui/icons/PeopleAlt';
import EmojiObjectsIcon from '@material-ui/icons/EmojiObjects';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CloseIcon from '@material-ui/icons/Close';
import LockIcon from '@material-ui/icons/Lock';
import InfoIcon from '@material-ui/icons/Info';
import ErrorIcon from '@material-ui/icons/Error';

import { quoteAssignmentService, assignToOrgService } from '../../../services/client.service';
import { toast } from '../../../utils/utils';
import { TOAST_TYPES } from '../../../utils/constants';

/**
 * AssignWithTokensDialog — a DQ Pro partner gives licences or credits to one of its
 * networks or schools, and sees the DQ Token conversion before assigning.
 *
 * Three steps, as on Ian's mockup (bug deck LA-PW-12-Oct-03, slide 59 onward):
 *   1. choose what to assign — licenses or LearnCreator credits;
 *   2. enter how many, with the cost and the balances before and after;
 *   3. confirm, then assign.
 * One kind per assignment; the other is sent as 0.
 *
 * The DQ Tokens are converted on the spot at the plan's prices — assignments come from
 * DQ Tokens only (product-owner decision, 17 Sep 2026). Every figure shown — the price,
 * the DQ Tokens after, the recipient's pool after — comes from the server's quote, so
 * what is assigned is exactly what was shown.
 *
 * Credits go to a network or a standalone school only: a school inside a network
 * shares its network's credits, so the Credits option is disabled for one.
 */

const ORANGE = '#FF6900';
const ORANGE_TINT = 'rgba(255,105,0,0.08)';
const TEAL = '#0097b2';
const TEXT = 'var(--brand-text, #111827)';
const MUTED = 'var(--brand-text-muted, #6B7280)';
const BORDER = 'var(--brand-border, #E5E7EB)';
const SURFACE = 'var(--brand-surface, #fff)';
const QUOTE_DELAY_MS = 300;

const LICENSES = 'licenses';
const CREDITS = 'credits';

const fmt = (n) => Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 2 });

/** Licences: a whole number. */
const toCount = (text) => {
    const v = String(text ?? '').trim();
    if (v === '') return 0;
    return /^\d+$/.test(v) ? Number(v) : null;
};

/** Credits: up to two decimals (23 Sep 2026). */
const toCredits = (text) => {
    const v = String(text ?? '').trim();
    if (v === '') return 0;
    return /^\d+(\.\d{1,2})?$/.test(v) ? Number(v) : null;
};

/** Keep digits and one decimal point with at most two decimals, as the partner types. */
const cleanCredits = (text) => {
    let v = String(text || '').replace(/[^\d.]/g, '');
    const dot = v.indexOf('.');
    if (dot >= 0) v = v.slice(0, dot + 1) + v.slice(dot + 1).replace(/\./g, '').slice(0, 2);
    return v.slice(0, 10);
};

const KIND_LABEL = { network: 'school network', school_in_network: 'school', standalone_school: 'school' };

/** One of the two step-1 choices — an icon tile, the copy, and the price (or why it is locked). */
const Choice = ({ selected, disabled, icon, title, description, price, per, lockedText, onClick }) => (
    <ButtonBase
        onClick={onClick}
        disabled={disabled}
        aria-pressed={selected}
        sx={{
            flex: 1, position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'stretch',
            textAlign: 'left', p: 2, borderRadius: '16px',
            border: `2px solid ${selected ? ORANGE : BORDER}`,
            backgroundColor: disabled ? 'var(--brand-bg, #F7F7F8)' : (selected ? ORANGE_TINT : SURFACE),
            boxShadow: selected ? '0 6px 18px rgba(255,105,0,0.14)' : 'none',
            transition: 'border-color .15s, background-color .15s, box-shadow .15s, transform .15s',
            '&:hover': disabled ? {} : { borderColor: ORANGE, transform: 'translateY(-1px)' },
            '&.Mui-focusVisible': { outline: `3px solid ${ORANGE_TINT}`, outlineOffset: 2 },
        }}
    >
        {selected && (
            <CheckCircleIcon style={{ position: 'absolute', top: 12, right: 12, fontSize: 20, color: ORANGE }}/>
        )}
        <Box sx={{
            width: 40, height: 40, borderRadius: '12px', mb: 1.5,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            backgroundColor: disabled ? BORDER : ORANGE,
            color: '#fff',
        }}>
            {icon}
        </Box>
        <Typography sx={{ fontSize: '15px', fontWeight: 800, color: disabled ? MUTED : TEXT, mb: 0.75 }}>{title}</Typography>
        <Typography sx={{ fontSize: '13px', color: MUTED, lineHeight: 1.5, flexGrow: 1, opacity: disabled ? 0.7 : 1 }}>{description}</Typography>
        {disabled ? (
            <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start', mt: 1.5 }}>
                <LockIcon style={{ fontSize: 15, color: 'var(--brand-text, #374151)', marginTop: 2 }}/>
                <Typography sx={{ fontSize: '12.5px', color: TEXT, lineHeight: 1.45 }}>{lockedText}</Typography>
            </Box>
        ) : price != null && (
            <Typography sx={{ fontSize: '12.5px', color: MUTED, mt: 1.5 }}>
                Price: <Box component="strong" sx={{ color: TEXT }}>{fmt(price)} DQ Tokens</Box> per {per}
            </Typography>
        )}
    </ButtonBase>
);

/** Three segments, filled up to the current step. */
const StepBar = ({ step }) => (
    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.75, mb: 2 }}>
        {[1, 2, 3].map((n) => (
            <Box key={n} sx={{
                width: 28, height: 4, borderRadius: '4px',
                backgroundColor: n <= step ? ORANGE : BORDER, transition: 'background-color .2s',
            }}/>
        ))}
    </Box>
);

const SummaryLine = ({ label, value, divider = true }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 2, py: 1.1, borderBottom: divider ? `1px solid ${BORDER}` : 'none' }}>
        <Typography sx={{ fontSize: '13px', color: MUTED }}>{label}</Typography>
        <Typography sx={{ fontSize: '13px', fontWeight: 700, color: TEXT, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
    </Box>
);

const pillBtn = {
    borderRadius: '999px', minWidth: '130px', py: 1, fontWeight: 700, textTransform: 'none', fontSize: '14px', boxShadow: 'none',
};
const primaryPill = {
    ...pillBtn, backgroundColor: ORANGE, color: '#fff',
    '&:hover': { backgroundColor: '#e55e00', boxShadow: '0 6px 16px rgba(255,105,0,0.3)' },
    '&.Mui-disabled': { backgroundColor: ORANGE, color: '#fff', opacity: 0.45 },
};
const backPill = { ...pillBtn, borderColor: TEAL, color: TEAL, '&:hover': { borderColor: TEAL, backgroundColor: 'rgba(0,151,178,0.06)' } };

/*
 * Three steps, as Ian revised them (bug deck LA-PW-12-Oct-03, slide 59 onward):
 *   1. what to assign — licenses or LearnCreator credits;
 *   2. how many, with the cost and the balances before and after;
 *   3. confirm.
 * `org.learncreator_linked === false` locks the credits card ("Link LearnCreator
 * to … to assign credits"); `onBuyTokens` is the "+ Buy more DQ Tokens" button.
 */
const AssignWithTokensDialog = ({ open, onClose, org, onAssigned, onBuyTokens }) => {
    const [step, setStep] = useState(1);
    const [kind, setKind] = useState(LICENSES);
    const [amount, setAmount] = useState('');
    // The "enter a number" error waits until the field has been used.
    const [touched, setTouched] = useState(false);
    const [quote, setQuote] = useState(null);
    const [quoting, setQuoting] = useState(false);
    const [assigning, setAssigning] = useState(false);
    const seq = useRef(0);

    const isCredits = kind === CREDITS;
    const nAmount = isCredits ? toCredits(amount) : toCount(amount);
    const validInput = nAmount !== null;
    const nLicences = !isCredits && validInput ? nAmount : 0;
    const nCredits = isCredits && validInput ? nAmount : 0;

    useEffect(() => {
        if (!open) return;
        setStep(1);
        setKind(LICENSES);
        setAmount('');
        setTouched(false);
        setQuote(null);
    }, [open, org?.org_code, org?.institute_code]);

    // Live quote, debounced; the latest request wins. With nothing entered it still
    // answers the recipient, the prices and the balances, which step 1 needs.
    useEffect(() => {
        if (!open || !org?.org_code || !validInput) return undefined;
        const mine = ++seq.current;
        setQuoting(true);
        const t = setTimeout(async () => {
            try {
                const res = await quoteAssignmentService({
                    org_code: org.org_code,
                    institute_code: org.institute_code || undefined,
                    licences: nLicences,
                    credits: nCredits,
                });
                if (mine === seq.current) setQuote(res?.data || null);
            } catch (_) {
                // invokeApi has already shown the server's reason (e.g. not the owner).
                if (mine === seq.current) setQuote(null);
            } finally {
                if (mine === seq.current) setQuoting(false);
            }
        }, QUOTE_DELAY_MS);
        return () => clearTimeout(t);
    }, [open, org?.org_code, org?.institute_code, nLicences, nCredits, validInput]);

    // A school inside a network shares its network's credits; and credits are for
    // LearnCreator, so an account without it has nothing to spend them on.
    const sharesNetworkCredits = quote ? quote.target?.can_receive_credits === false : false;
    const lcLinked = org?.learncreator_linked !== false;
    const creditsLocked = sharesNetworkCredits || !lcLinked;

    useEffect(() => { if (creditsLocked && kind === CREDITS) setKind(LICENSES); }, [creditsLocked, kind]);

    const chooseKind = (next) => {
        if (next === kind) return;
        setKind(next);
        setAmount('');
        setTouched(false);
    };

    if (!open || !org) return null;

    const q = quote;
    const name = q?.target?.org_name || org.org_name;
    const targetLabel = KIND_LABEL[q?.target?.kind] || 'school';
    const noun = isCredits ? 'credits' : 'licenses';
    const unit = (n) => (isCredits ? (Number(n) === 1 ? 'credit' : 'credits') : (Number(n) === 1 ? 'license' : 'licenses'));
    const unitPrice = isCredits ? q?.prices?.credit_price_dqt : q?.prices?.licence_price_dqt;
    const balance = q?.workspace?.before?.dqt;
    // How many the balance can pay for.
    const max = unitPrice > 0 && balance != null ? Math.floor(Number(balance) / Number(unitPrice)) : null;

    const tooFew = !validInput || nAmount < 1;
    const tooMany = validInput && max != null && nAmount > max;
    const coversText = max != null ? `Your balance covers up to ${fmt(max)} ${noun}.` : '';
    const fieldError = tooMany ? coversText : (touched && tooFew ? 'Enter a number of 1 or more.' : '');
    const amountOk = !tooFew && !tooMany;
    const quoteOk = !!q && q.allowed && !quoting;
    const cost = validInput && unitPrice != null ? nAmount * Number(unitPrice) : 0;
    // Licences are priced by the client's total (rate card tiers); null when not tiered.
    const tier = !isCredits ? (q?.licence_tier || null) : null;
    const usd = (n) => `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    const goConfirm = () => {
        setTouched(true);
        if (amountOk && quoteOk) setStep(3);
    };

    const handleAssign = async () => {
        if (!(amountOk && quoteOk) || assigning) return;
        setAssigning(true);
        try {
            const res = await assignToOrgService({
                org_code: org.org_code,
                institute_code: org.institute_code || undefined,
                licences: nLicences,
                credits: nCredits,
            });
            const d = res?.data;
            if (d) {
                const what = d.licences ? `${fmt(d.licences)} licenses` : `${fmt(d.credits)} credits`;
                toast(TOAST_TYPES.SUCCESS, 'Assigned',
                    `${what} assigned to ${d.target?.org_name}.${d.dqt_cost?.total ? ` ${fmt(d.dqt_cost.total)} DQ Tokens converted.` : ''}`);
                if (onAssigned) onAssigned(d);
                onClose();
            }
        } catch (_) {
            // invokeApi has already shown the server's reason; go back and refresh the quote.
            setQuote(null);
            seq.current += 1;
            setStep(2);
        } finally {
            setAssigning(false);
        }
    };

    const title = step === 1
        ? <>Assign to <Box component="span" sx={{ color: ORANGE }}>{name}</Box></>
        : step === 2 ? `How many ${noun} do you want to assign?` : 'Confirm assignment';

    return (
        <Dialog
            open={open} onClose={() => !assigning && onClose()} maxWidth="sm" fullWidth
            PaperProps={{ sx: {
                borderRadius: '24px', overflow: 'hidden', boxShadow: '0 25px 60px rgba(0,0,0,0.18)',
                backgroundColor: SURFACE, maxWidth: 560, position: 'relative',
            } }}
        >
            <IconButton
                aria-label="Close" size="small" disabled={assigning} onClick={onClose}
                sx={{ position: 'absolute', top: 14, right: 14, color: MUTED }}
            >
                <CloseIcon style={{ fontSize: 20 }}/>
            </IconButton>
            <DialogContent sx={{ px: { xs: 2.5, sm: 4 }, pt: '28px !important', pb: 1 }}>
                <StepBar step={step}/>
                <Typography component="h2" sx={{ color: TEXT, fontWeight: 800, textAlign: 'center', fontSize: '20px', mb: 0.5, px: 3 }}>
                    {title}
                </Typography>

                {step === 1 && (
                    <>
                        <Typography sx={{ textAlign: 'center', fontSize: '14px', color: MUTED, lineHeight: 1.5, mb: 2.5 }}>
                            What would you like to assign to this {targetLabel}?
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5 }}>
                            <Choice
                                selected={kind === LICENSES}
                                onClick={() => chooseKind(LICENSES)}
                                icon={<PeopleAltIcon style={{ fontSize: 22 }}/>}
                                title="Licenses"
                                description={`One license lets one student take the programs linked to ${name}.`}
                                price={q?.prices?.licence_price_dqt}
                                per="license"
                            />
                            <Choice
                                selected={kind === CREDITS}
                                disabled={creditsLocked}
                                onClick={() => chooseKind(CREDITS)}
                                icon={<EmojiObjectsIcon style={{ fontSize: 22 }}/>}
                                title="LearnCreator credits"
                                description={`One credit lets ${name} create one 30–45 minute session in LearnCreator.`}
                                price={q?.prices?.credit_price_dqt}
                                per="credit"
                                lockedText={sharesNetworkCredits
                                    ? 'Uses its network\'s credits.'
                                    : `Link LearnCreator to ${name} to assign credits.`}
                            />
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2.5, minHeight: 24 }}>
                            {q ? (
                                <Typography sx={{ fontSize: '13px', color: MUTED }}>
                                    Your DQ Token balance: <Box component="strong" sx={{ color: TEXT }}>{fmt(balance)}</Box>
                                </Typography>
                            ) : quoting && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '13px', color: MUTED }}>
                                    <CircularProgress size={14} style={{ color: ORANGE }}/> Loading your DQ Tokens…
                                </Box>
                            )}
                        </Box>
                    </>
                )}

                {step === 2 && (
                    <>
                        <Typography sx={{ textAlign: 'center', fontSize: '13.5px', color: ORANGE, mb: 2 }}>{name}</Typography>

                        <Typography sx={{ fontSize: '13px', fontWeight: 700, color: TEXT, mb: 0.75 }}>Number of {noun}</Typography>
                        <TextField
                            fullWidth autoFocus
                            value={amount}
                            error={!!fieldError}
                            onChange={(e) => {
                                setTouched(true);
                                setAmount(isCredits
                                    ? cleanCredits(e.target.value)
                                    : e.target.value.replace(/[^\d]/g, '').slice(0, 7));
                            }}
                            inputProps={{ inputMode: isCredits ? 'decimal' : 'numeric', 'aria-label': `Number of ${noun}` }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '10px', fontSize: '16px', fontWeight: 700,
                                    '& fieldset': { borderColor: fieldError ? '#D32F2F' : ORANGE, borderWidth: '1.5px' },
                                    '&:hover fieldset': { borderColor: fieldError ? '#D32F2F' : ORANGE },
                                    '&.Mui-focused fieldset': { borderColor: fieldError ? '#D32F2F' : ORANGE },
                                },
                            }}
                        />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.75, minHeight: 20 }}>
                            {fieldError && <ErrorIcon style={{ fontSize: 15, color: '#D32F2F' }}/>}
                            <Typography sx={{ fontSize: '12.5px', color: fieldError ? '#D32F2F' : MUTED }}>
                                {fieldError || coversText}
                            </Typography>
                        </Box>

                        {onBuyTokens && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.5 }}>
                                <Button
                                    variant="outlined" onClick={onBuyTokens}
                                    sx={{ borderRadius: '999px', textTransform: 'none', fontWeight: 700, fontSize: '13px', px: 2.25, borderColor: ORANGE, color: ORANGE, '&:hover': { borderColor: ORANGE, backgroundColor: ORANGE_TINT } }}
                                >
                                    + Buy more DQ Tokens
                                </Button>
                            </Box>
                        )}

                        {validInput && nAmount > 0 && unitPrice != null && (
                            <Box sx={{ mt: 2, px: 1.75, py: 1.25, borderRadius: '10px', backgroundColor: ORANGE_TINT }}>
                                <Typography sx={{ fontSize: '13px', color: TEXT }}>
                                    Cost: {fmt(nAmount)} {unit(nAmount)} × {fmt(unitPrice)} DQ Tokens = <strong>{fmt(cost)} DQ Tokens</strong>
                                </Typography>
                            </Box>
                        )}

                        {tier && amountOk && quoteOk && (
                            <Box sx={{ mt: 1.25, px: 1.75, py: 1.25, borderRadius: '10px', border: `1px solid ${BORDER}` }}>
                                <Typography sx={{ fontSize: '13px', color: TEXT }}>
                                    Price tier: <strong>{tier.name}</strong> ({tier.range} licenses), {usd(tier.usd_per_licence)} per license.
                                    {' '}{name} will have {fmt(tier.client_licences_after)} licenses in total.
                                </Typography>
                                {tier.next && (
                                    <Typography sx={{ fontSize: '12.5px', color: MUTED, mt: 0.5 }}>
                                        {fmt(tier.next.licences_to_go)} more licenses for {name} reach the {tier.next.name} tier: {usd(tier.next.usd_per_licence)} per license.
                                    </Typography>
                                )}
                            </Box>
                        )}

                        {/* Before and after — the server's figures, shown once the amount is one it accepts. */}
                        <Box sx={{ mt: 2 }}>
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 90px 90px', gap: 1, pb: 0.75 }}>
                                <span/>
                                <Typography sx={{ fontSize: '12px', color: MUTED, textAlign: 'right' }}>Before</Typography>
                                <Typography sx={{ fontSize: '12px', color: MUTED, textAlign: 'right' }}>After</Typography>
                            </Box>
                            {[
                                ['Your DQ Token balance', q?.workspace?.before?.dqt, q?.workspace?.after?.dqt],
                                [`${name}’s ${noun}`,
                                    isCredits ? q?.recipient?.credits_before : q?.recipient?.licences_before,
                                    isCredits ? q?.recipient?.credits_after : q?.recipient?.licences_after],
                            ].map(([label, before, after]) => (
                                <Box key={label} sx={{ display: 'grid', gridTemplateColumns: '1fr 90px 90px', gap: 1, py: 1, borderTop: `1px solid ${BORDER}`, alignItems: 'baseline' }}>
                                    <Typography sx={{ fontSize: '13px', color: TEXT }}>{label}</Typography>
                                    <Typography sx={{ fontSize: '13px', color: MUTED, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{before != null ? fmt(before) : '—'}</Typography>
                                    <Typography sx={{ fontSize: '13px', fontWeight: 700, color: TEXT, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                                        {amountOk && quoteOk && after != null ? fmt(after) : '—'}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                        {q && !q.allowed && amountOk && q.blocked_reason !== 'NOTHING_TO_ASSIGN' && (
                            <Typography sx={{ mt: 1, fontSize: '12.5px', color: '#b45309' }}>{q.message}</Typography>
                        )}
                    </>
                )}

                {step === 3 && (
                    <>
                        <Box sx={{ mt: 2 }}>
                            <SummaryLine label="To" value={name}/>
                            <SummaryLine label="Assigning" value={`${fmt(nAmount)} ${unit(nAmount)}`}/>
                            {tier && <SummaryLine label="Price tier" value={`${tier.name} — ${usd(tier.usd_per_licence)} per license`}/>}
                            <SummaryLine label="Cost" value={`${fmt(q?.dqt_cost?.total ?? cost)} DQ Tokens`}/>
                            <SummaryLine label="Remaining balance" value={`${fmt(q?.workspace?.after?.dqt)} DQ Tokens`} divider={false}/>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mt: 2 }}>
                            <InfoIcon style={{ fontSize: 16, color: MUTED, marginTop: 2 }}/>
                            <Typography sx={{ fontSize: '12.5px', color: MUTED, lineHeight: 1.5 }}>
                                {name} can use these {noun} right away. You can’t undo an assignment.
                            </Typography>
                        </Box>
                    </>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 3, pt: 2, justifyContent: 'center', gap: 1.5 }}>
                <Button
                    variant="outlined" disabled={assigning}
                    onClick={() => (step === 1 ? onClose() : setStep(step - 1))}
                    sx={backPill}
                >
                    {step === 1 ? 'Cancel' : 'Back'}
                </Button>
                {step === 1 && (
                    <Button variant="contained" disabled={!q} onClick={() => setStep(2)} sx={primaryPill}>Next</Button>
                )}
                {step === 2 && (
                    <Button variant="contained" disabled={!q || quoting || tooMany} onClick={goConfirm} sx={primaryPill}>Next</Button>
                )}
                {step === 3 && (
                    <Button variant="contained" disabled={assigning || !(amountOk && quoteOk)} onClick={handleAssign} sx={primaryPill}>
                        {assigning ? <CircularProgress size={18} style={{ color: '#fff' }}/> : `Assign ${fmt(nAmount)} ${unit(nAmount)}`}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default AssignWithTokensDialog;
